import { useMemo, useState } from 'react';

/**
 * In-memory counterpart of useServerTable for small, already-loaded lists (dashboard
 * reports). Same `tableProps` contract, so it drives <DataTable /> unchanged.
 *
 * searchKeys: row keys matched by the search box.
 * sortValue(row, key): optional accessor; defaults to row[key].
 */
export default function useClientTable(
    allRows,
    { searchKeys = [], defaultSort = null, defaultLimit = 20, sortValue } = {}
) {
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(defaultLimit);
    const [sort, setSort] = useState(defaultSort);
    const [search, setSearch] = useState('');

    const filtered = useMemo(() => {
        const needle = search.trim().toLowerCase();
        let out = allRows || [];
        if (needle) {
            out = out.filter((r) =>
                searchKeys.some((k) =>
                    String(r[k] ?? '')
                        .toLowerCase()
                        .includes(needle)
                )
            );
        }
        if (sort?.key) {
            const sign = sort.dir === 'desc' ? -1 : 1;
            const val = (r) => (sortValue ? sortValue(r, sort.key) : r[sort.key]);
            out = [...out].sort((a, b) => {
                const x = val(a);
                const y = val(b);
                if (x == null && y == null) return 0;
                if (x == null) return 1; // blanks last in both directions
                if (y == null) return -1;
                const nx = Number(x);
                const ny = Number(y);
                if (!Number.isNaN(nx) && !Number.isNaN(ny)) return (nx - ny) * sign;
                return String(x).localeCompare(String(y), undefined, { numeric: true }) * sign;
            });
        }
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allRows, search, sort]);

    const total = filtered.length;
    const totalPages = Math.ceil(total / limit);
    const current = Math.min(page, Math.max(totalPages, 1));
    const rows = filtered.slice((current - 1) * limit, current * limit);

    const onSort = (key) => {
        setPage(1);
        setSort((prev) => {
            if (prev?.key !== key) return { key, dir: 'asc' };
            if (prev.dir === 'asc') return { key, dir: 'desc' };
            return defaultSort;
        });
    };

    return {
        filtered,
        tableProps: {
            rows,
            loading: false,
            error: '',
            sort,
            onSort,
            search,
            onSearchChange: (v) => {
                setSearch(v);
                setPage(1);
            },
            pagination: { page: current, limit, total, totalPages },
            onPageChange: setPage,
            onLimitChange: (n) => {
                setLimit(n);
                setPage(1);
            },
            hasActiveFilters: Boolean(search),
            onResetFilters: () => {
                setSearch('');
                setPage(1);
            },
        },
    };
}
